/**
 * `<OutlineCommandMenu/>` — the inline "/" command menu for an outline row
 * (#1043). Pressing `/` on an empty row opens this compact menu offering the
 * two row-level actions from the founder's shortcut screenshot:
 *
 *   - **Change state** → expands to the four factual states (Not started /
 *     In progress / Blocked / Done); picking one calls `onSetState(state)`.
 *     Reuses the shared state vocabulary (`tree/state` `TASK_STATES`) + the
 *     theme-aware `<StatusDot/>` so a state reads identically everywhere; the
 *     state-change itself goes through the SAME `patchNode` path the canvas
 *     card + drawer use (wired in `MapEditor` as `onSetState`).
 *   - **Add a dependency** → arms click-to-link FROM this row (`onArmLink(id)`),
 *     so the next node the user clicks on the canvas becomes the "comes after"
 *     target. Reuses the existing click-to-link mechanism (`treeStore.linkArm`
 *     / the canvas's link gesture, #904) — the menu doesn't duplicate any
 *     dependency logic, it just primes the same arm the canvas reads. Only
 *     present when the dependency layer is on (`onAddDependency` supplied).
 *
 * Brand (§A9): factual copy, Blocked renders in calm amber (`stateStyle`),
 * never alarm red. Theme-aware via `useMapTheme()`; legacy + editorial both
 * render the same structure + copy + data-testids, only the look forks.
 *
 * The menu is presentational: every choice is a callback the `<OutlineEditor/>`
 * wires to a mutation / arm. It owns no tree maths and no network.
 */

import { useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { TASK_STATES, type TaskState } from "../tree/state";
import { StatusDot } from "./StatusChip";

export interface OutlineCommandMenuProps {
  /** The node's current state (gets the ✓ in the state sub-list). */
  current: TaskState;
  /** Set the row's state (→ the same PATCH the card / drawer use). */
  onSetState: (state: TaskState) => void;
  /**
   * Arm "add a dependency" FROM this row (the click-to-link source, #904). The
   * next node clicked on the canvas becomes what this row comes before. Omitted
   * when the dependency layer is off — the menu then hides that item entirely.
   */
  onAddDependency?: (() => void) | undefined;
  /** Dismiss the menu (Escape / a click away / after a choice). */
  onClose: () => void;
}

export function OutlineCommandMenu({
  current,
  onSetState,
  onAddDependency,
  onClose,
}: OutlineCommandMenuProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // Two top-level commands; "Change state" drills into the four states.
  const [stateStep, setStateStep] = useState(false);

  const pickState = (state: TaskState) => {
    onSetState(state);
    onClose();
  };

  const wrapperStyle = editorial
    ? {
        borderColor: c.rule,
        backgroundColor: c.cardBg,
        borderRadius: theme.radii.lg,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        fontFamily: theme.fonts.sans,
      }
    : { borderColor: PALETTE.cardBorder };

  const headerLabel = stateStep ? "Set state" : "Commands";

  return (
    <div
      data-testid="outline-command-menu"
      className={
        editorial
          ? "absolute z-30 mt-1 w-[230px] overflow-hidden rounded-lg border"
          : "absolute z-30 mt-1 w-[222px] rounded-lg border bg-white p-2 shadow-xl"
      }
      style={wrapperStyle}
      role="menu"
      aria-label={headerLabel}
    >
      {/* Header — mono kicker (editorial) / bold caps (legacy). */}
      <div
        className={
          editorial
            ? "border-b px-3.5 py-2.5 text-[10px] font-medium uppercase"
            : "px-1 pb-1 text-[10px] font-bold uppercase tracking-wide"
        }
        style={
          editorial
            ? {
                borderColor: c.rule,
                fontFamily: theme.fonts.mono,
                letterSpacing: "0.1em",
                color: c.muted,
              }
            : { color: PALETTE.muted }
        }
      >
        {headerLabel}
      </div>

      {stateStep ? (
        <div className={editorial ? "" : "mt-1 flex flex-col gap-1"}>
          {TASK_STATES.map((s) => {
            const style = theme.stateStyle(s.state);
            const isCurrent = s.state === current;
            return (
              <button
                key={s.state}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                data-testid={`outline-command-state-${s.state}`}
                onClick={() => pickState(s.state)}
                className={
                  editorial
                    ? "flex w-full items-center gap-2.5 border-b border-dashed px-3.5 py-2.5 text-left text-[13px] transition-colors last:border-b-0 hover:opacity-90"
                    : "flex items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors hover:opacity-90"
                }
                style={
                  editorial
                    ? {
                        borderColor: c.rule,
                        backgroundColor: isCurrent ? c.bgRecess : "transparent",
                        color: c.ink,
                        fontWeight: isCurrent ? 600 : 400,
                      }
                    : {
                        // Blocked carries its calm amber chip here too (§A9).
                        backgroundColor: isCurrent ? style.chipBg : "transparent",
                        color: style.fg,
                        fontWeight: isCurrent ? 600 : 400,
                      }
                }
              >
                <span className="flex items-center gap-2">
                  <StatusDot state={s.state} />
                  <span>{s.label}</span>
                </span>
                {isCurrent && (
                  <span
                    aria-hidden
                    className={editorial ? "ml-auto" : ""}
                    style={{ color: style.fg }}
                  >
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={editorial ? "" : "mt-1 flex flex-col gap-1"}>
          <button
            type="button"
            role="menuitem"
            data-testid="outline-command-change-state"
            onClick={() => setStateStep(true)}
            className={
              editorial
                ? "flex w-full items-center gap-2.5 border-b border-dashed px-3.5 py-2.5 text-left text-[13px] transition-colors hover:opacity-90"
                : "flex items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors hover:bg-slate-50"
            }
            style={editorial ? { borderColor: c.rule, color: c.ink } : { color: PALETTE.ink }}
          >
            <span>Change state</span>
            <span
              aria-hidden
              className="ml-auto"
              style={{ color: editorial ? c.muted : PALETTE.muted }}
            >
              →
            </span>
          </button>
          {onAddDependency && (
            <button
              type="button"
              role="menuitem"
              data-testid="outline-command-add-dependency"
              onClick={() => {
                onAddDependency();
                onClose();
              }}
              className={
                editorial
                  ? "flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] transition-colors hover:opacity-90"
                  : "flex items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors hover:bg-slate-50"
              }
              style={editorial ? { color: c.ink } : { color: PALETTE.ink }}
            >
              <span>Add a dependency</span>
              <span
                aria-hidden
                className="ml-auto"
                style={{ color: editorial ? c.muted : PALETTE.muted }}
              >
                ⇢
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
